import type { Theme, SxStyles } from 'geoview-core/ui/style/types';
import { geoViewColors as defaultGeoViewColors } from 'geoview-core/ui/style/default';
import { getFocusIndicatorStyles } from 'geoview-core/ui/style/themeOptionsGenerator';

/**
 * Returns the sx style classes for the Custom Legend components.
 *
 * @param theme - The MUI theme object
 * @returns The sx style classes
 */
export const getSxClasses = (theme: Theme): SxStyles => ({
  // Main container styles
  container: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: theme.palette.surface?.high ?? theme.palette.background.paper,
  },

  // List container for legend items
  legendList: {
    paddingRight: theme.spacing(1.25),
  },

  // Individual legend list item
  legendListItem: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& .MuiCollapse-vertical': {
      marginLeft: theme.spacing(0.75),
    },
    // Override MUI ListItemButton's action.hover background to keep legend rows flat.
    '& .MuiListItemButton-root:hover': {
      backgroundColor: 'transparent',
    },
  },

  // Used by HeaderItem component
  headerItem: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 0.5),
  },

  // Header text styling
  headerText: {
    fontSize: theme.palette.geoViewFontSize?.md,
    fontWeight: '600',
    color: theme.palette.text.primary,
    maxWidth: '400px',
  },

  // Group children container
  groupChildren: {
    paddingLeft: theme.spacing(2),
    marginTop: theme.spacing(0),
    width: '100%',
  },

  // Group title styling
  groupTitle: {
    textWrapMode: 'wrap',
    fontSize: '1.125rem', // 18px
    fontWeight: '700',
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },

  // Group item button
  groupItemButton: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 0.5),
    width: '100%',
  },

  // Group icon styling
  groupIcon: {
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(0.25),
    width: '24px',
    height: '24px',
    transform: 'scaleX(-1)', // Mirror to face right
    borderRadius: theme.shape.borderRadiusMd,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  groupItemContent: {
    margin: theme.spacing(0.75, 0.75, 0.75, 1.5),
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
  },

  // Full-width wrapper for the group item body
  groupItemWrapper: {
    width: '100%',
  },

  // Wrapper around the group icon inside ListItemIcon
  groupIconListItemIcon: {
    minWidth: '24px',
  },

  // Row containing the group title area (title, subtext, visibility button)
  groupTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
  },

  // Small action icon buttons (visibility toggle, collapse toggle)
  groupActionIconButton: {
    padding: theme.spacing(0.5),
  },

  // Wrapper keeping the collapse button vertically centered
  groupCollapseButtonWrapper: {
    alignSelf: 'center',
  },

  // Collapsible children container spacing (Collapse component)
  groupChildrenCollapse: {
    marginRight: theme.spacing(0.75),
  },

  // Sublayer count caption
  groupSubLayerCount: {
    display: 'block',
    color: theme.palette.text.primary,
    fontSize: '14px',
    margin: theme.spacing(0),
  },

  // Group button row with smaller icons
  groupButtonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
    },
  },

  // Collapse button container
  groupCollapseButton: {
    display: 'flex',
    alignItems: 'center',
  },

  descriptionContainer: {
    display: 'flex',
    flexDirection: 'row',
  },

  descriptionToggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },

  descriptionToggleButton: {
    padding: theme.spacing(0, 0.25, 0, 0),
    minWidth: 'auto',
    fontSize: '0.75rem',
    fontStyle: 'italic',
    fontWeight: 300,
    textTransform: 'none',
    color: theme.palette.text.secondary,
    // Override MUI text-button hover background to keep this disclosure control flat.
    '&:hover': {
      backgroundColor: 'transparent',
    },
    ...(!theme.focusVisible && {
      '&:focus-visible': {
        ...getFocusIndicatorStyles(theme.palette.geoViewColor ?? defaultGeoViewColors),
        border: 'none !important',
      },
    }),
  },

  descriptionText: {
    fontSize: '0.875rem',
    fontStyle: 'italic',
    fontWeight: 300,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(2),
  },

  // Reset margins for description text (important for group descriptions)
  descriptionCollapse: {
    marginLeft: `${theme.spacing(0)} !important`,
    '& .MuiCollapse-vertical': {
      marginLeft: `${theme.spacing(0)} !important`,
    },
  },
});
